import React, { useState, useCallback, useMemo } from 'react';
import { useNotify, useDataProvider } from 'react-admin';
import { LEAD_VALIDATION_RULES, createFieldValidator, createFormValidator } from '../utils/leadValidators';
import logger from '../../utils/logger';

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  language: string;
  birthDate: string;
  sourceOrgId: string;
  status: string;
  smsOptIn: boolean;
  wicClinicCode: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  phoneNumber: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  language: '',
  birthDate: '',
  sourceOrgId: '',
  status: 'active',
  smsOptIn: true,
  wicClinicCode: '',
};

interface UseCreateLeadModalReturn {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: Record<string, string | undefined>;
  isSubmitting: boolean;
  validateField: (field: string, value: any) => string | undefined;
  validateForm: () => Record<string, string | undefined>;
  resetForm: () => void;
  handleSubmit: () => Promise<void>;
}

export const useCreateLeadModal = (onSuccess?: (data: any) => void, onClose?: () => void): UseCreateLeadModalReturn => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const validateField = useCallback((field, value) => {
    const error = createFieldValidator(LEAD_VALIDATION_RULES)(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
    return error;
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = createFormValidator(LEAD_VALIDATION_RULES)(formData);
    setErrors(newErrors);
    return newErrors;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setIsSubmitting(false);
  }, []);

  // Check if form is valid for submission (computed value, not function)
  // Use a version that doesn't update state to avoid re-render loops
  const isFormValid = useMemo(() => {
    // Check required fields
    if (!formData.name?.trim()) return false;
    if (!formData.state?.trim()) return false;
    if (!formData.address?.trim()) return false;
    if (!formData.zipCode?.trim()) return false;
    if (!formData.sourceOrgId) return false;

    // Check that at least one contact method is provided
    const email = formData.email?.trim();
    const phoneNumber = formData.phoneNumber?.trim();
    if (!email && !phoneNumber) return false;

    // Check for validation errors (without updating state)
    const validationErrors = createFormValidator(LEAD_VALIDATION_RULES)(formData);
    return Object.keys(validationErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);

      // Prepare data for API
      const apiData = {
        name: formData.name.trim(),
        email: formData.email?.trim() ? formData.email.trim().toLowerCase() : null,
        phoneNumber: formData.phoneNumber?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        zip_code: formData.zipCode?.trim() || null,
        language: formData.language?.trim() || null,
        birthDate: formData.birthDate || null,
        sourceOrgId: formData.sourceOrgId || null,
        status: formData.status,
        smsOptIn: formData.smsOptIn,
        wicClinicCode: formData.wicClinicCode?.trim() || null,
      };

      // Remove null values
      Object.keys(apiData).forEach(key => {
        if (apiData[key] === null || apiData[key] === '') {
          delete apiData[key];
        }
      });

      // Use data provider to create lead
      const response = await dataProvider.create('leads', { data: apiData });

      notify('Lead created successfully', { type: 'success' });

      // Call onSuccess before closing to ensure list refresh
      onSuccess?.(response.data);

      // Reset form and close modal
      resetForm();
      onClose?.();

    } catch (error) {
      logger.error('Error creating lead:', error);
      notify(error.message || 'Failed to create lead', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, dataProvider, notify, onSuccess, onClose]);

  return {
    formData,
    setFormData,
    errors,
    isSubmitting,
    validateField,
    validateForm,
    resetForm,
    handleSubmit,
  };
};
